import React, { useState, useEffect, useRef } from 'react';
import Layout from '../../components/Layout';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const C = { teal:'#00B4D8', dark:'#1F2937', grey:'#6B7280', border:'#E5E7EB', gold:'#C9A84C' };

export default function AdminCertificates() {
  const [tenant, setTenant] = useState(null);
  const [form, setForm] = useState({ principal_name:'', principal_title:'School Principal' });
  const [sigFile, setSigFile] = useState(null);
  const [sigPreview, setSigPreview] = useState(null);
  const [hasExisting, setHasExisting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sigText, setSigText] = useState('');
  const [sigFont, setSigFont] = useState('Great Vibes');
  const fileRef = useRef();

  // Self-hosted handwriting fonts for the typed-signature option (CSP-safe).
  useEffect(() => {
    const id = 'sig-fonts';
    if (!document.getElementById(id)) {
      const st = document.createElement('style');
      st.id = id;
      st.textContent = "@font-face{font-family:'Great Vibes';src:url('/fonts/GreatVibes.woff2') format('woff2');font-display:swap;}"
        + "@font-face{font-family:'Dancing Script';src:url('/fonts/DancingScript.woff2') format('woff2');font-display:swap;}";
      document.head.appendChild(st);
    }
  }, []);

  const SIG_FONTS = ['Great Vibes', 'Dancing Script'];

  // Remove the (light) background from an uploaded signature photo — keep only the ink.
  const removeBackground = (file) => new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const maxW = 700;
      const scale = img.naturalWidth > maxW ? maxW / img.naturalWidth : 1;
      const cv = document.createElement('canvas');
      cv.width = Math.round(img.naturalWidth * scale); cv.height = Math.round(img.naturalHeight * scale);
      const ctx = cv.getContext('2d');
      ctx.drawImage(img, 0, 0, cv.width, cv.height);
      const d = ctx.getImageData(0, 0, cv.width, cv.height);
      const a = d.data;
      const t1 = 0.32, t2 = 0.48;   // only genuinely dark ink survives; grey haze removed
      for (let i = 0; i < a.length; i += 4) {
        const lum = (0.299 * a[i] + 0.587 * a[i + 1] + 0.114 * a[i + 2]) / 255;
        let alpha;
        if (lum <= t1) alpha = 255;
        else if (lum >= t2) alpha = 0;
        else alpha = Math.round((t2 - lum) / (t2 - t1) * 255);
        if (alpha < 70) alpha = 0;   // drop faint grey pixels entirely
        a[i] = 21; a[i + 1] = 35; a[i + 2] = 74; a[i + 3] = alpha;   // ink → navy
      }
      ctx.putImageData(d, 0, 0);
      cv.toBlob(b => b ? resolve(new File([b], 'signature.png', { type: 'image/png' })) : reject(), 'image/png');
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });

  const makeTypedSignature = async () => {
    const name = (form.principal_name || '').trim();
    if (!name) { toast.error('Enter the Principal Name above first'); return; }
    try { await document.fonts.load(`64px "${sigFont}"`); await document.fonts.ready; } catch (e) {}
    const canvas = document.createElement('canvas');
    canvas.width = 600; canvas.height = 170;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#15234a';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    let size = 90; ctx.font = `${size}px "${sigFont}"`;
    while (ctx.measureText(name).width > 560 && size > 24) { size -= 4; ctx.font = `${size}px "${sigFont}"`; }
    ctx.fillText(name, 300, 90);
    canvas.toBlob((blob) => {
      if (!blob) { toast.error('Could not render the signature'); return; }
      setSigFile(new File([blob], 'typed-signature.png', { type: 'image/png' }));
      setSigPreview(URL.createObjectURL(blob));
      toast.success('Signature ready — click Save to apply');
    }, 'image/png');
  };

  useEffect(() => {
    api.get('/api/admin/dashboard').then(r => {
      setTenant(r.data?.tenant);
    });
    api.get('/api/admin/certificates/settings').then(r => {
      setForm({ principal_name: r.data.principal_name || '', principal_title: r.data.principal_title || 'School Principal' });
      setHasExisting(r.data.has_signature);
    }).catch(() => {});
  }, []);

  const handleFile = async e => {
    const f = e.target.files[0];
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) { toast.error('Image too large — max 4MB'); return; }
    if (!f.type.startsWith('image/')) { toast.error('Please select a PNG or JPG image'); return; }
    try {
      const cleaned = await removeBackground(f);   // strip the paper/photo background
      setSigFile(cleaned);
      setSigPreview(URL.createObjectURL(cleaned));
      toast.success('Background removed — only the signature kept');
    } catch {
      setSigFile(f);
      setSigPreview(URL.createObjectURL(f));
    }
  };

  const removeSignature = async () => {
    try {
      await api.delete('/api/admin/certificates/signature');
      setSigFile(null); setSigPreview(null); setHasExisting(false);
      toast.success('Uploaded signature removed — the typed handwriting will be used');
    } catch { toast.error('Could not remove signature'); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('principal_name', form.principal_name);
      fd.append('principal_title', form.principal_title);
      if (sigFile) fd.append('signature', sigFile);
      await api.post('/api/admin/certificates/signature', fd);
      toast.success('Certificate settings saved!');
      if (sigFile) setHasExisting(true);
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const [previewing, setPreviewing] = useState(false);
  const previewCert = async () => {
    setPreviewing(true);
    try {
      const params = new URLSearchParams({
        principal_name: form.principal_name || '',
        principal_title: form.principal_title || '',
        level: 'B2',
      });
      const r = await api.get('/api/admin/certificates/preview?' + params.toString(), { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error('Could not generate preview'); }
    finally { setPreviewing(false); }
  };

  return (
    <Layout role="tenant_admin" tenantColor={tenant?.primary_colour} tenantName={tenant?.name}>
      <div style={{ maxWidth:640 }}>
        <h2 style={{ margin:'0 0 6px', fontSize:22, fontWeight:800 }}>Certificate Settings</h2>
        <p style={{ margin:'0 0 28px', color:C.grey }}>Configure signatures that appear on all certificates issued by your school</p>

        <div className="card" style={{ padding:28, marginBottom:20 }}>
          <h3 style={{ margin:'0 0 20px', fontSize:16, fontWeight:700 }}>Principal / Authorised Signatory</h3>
          
          <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
            <div>
              <label style={{ fontSize:13, fontWeight:600, color:C.grey, display:'block', marginBottom:6 }}>Principal Name</label>
              <input value={form.principal_name} onChange={e => setForm({...form, principal_name:e.target.value})}
                placeholder="e.g. Dr. Sarah Johnson"
                style={{ width:'100%', padding:'10px 12px', border:`1px solid ${C.border}`, borderRadius:8, fontSize:14, boxSizing:'border-box' }} />
            </div>

            <div>
              <label style={{ fontSize:13, fontWeight:600, color:C.grey, display:'block', marginBottom:6 }}>Title</label>
              <input value={form.principal_title} onChange={e => setForm({...form, principal_title:e.target.value})}
                placeholder="School Principal"
                style={{ width:'100%', padding:'10px 12px', border:`1px solid ${C.border}`, borderRadius:8, fontSize:14, boxSizing:'border-box' }} />
            </div>

            <div>
              <label style={{ fontSize:13, fontWeight:600, color:C.grey, display:'block', marginBottom:6 }}>Option A — Turn the Principal Name into a handwritten signature</label>
              <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
                <span style={{ fontSize:12, color:C.grey }}>Style:</span>
                {SIG_FONTS.map(f => (
                  <button key={f} onClick={() => setSigFont(f)}
                    style={{ padding:'6px 14px', borderRadius:8, border:`2px solid ${sigFont===f?C.teal:C.border}`, background:sigFont===f?'#EBF9FD':'#fff', cursor:'pointer', fontFamily:`'${f}', cursive`, fontSize:20, color:'#15234a' }}>
                    {(form.principal_name || 'Signature').slice(0,12)}
                  </button>
                ))}
              </div>
              <div style={{ marginTop:10, padding:'16px', border:`1px dashed ${C.border}`, borderRadius:10, textAlign:'center', background:'#FAFAFA' }}>
                <span style={{ fontFamily:`'${sigFont}', cursive`, fontSize:42, color:'#15234a', lineHeight:1.2 }}>{form.principal_name || 'Your name'}</span>
              </div>
              <button onClick={makeTypedSignature} style={{ marginTop:8, padding:'9px 18px', borderRadius:8, background:C.teal, color:'#fff', border:'none', fontWeight:700, fontSize:13, cursor:'pointer' }}>
                ✍️ Use this as signature
              </button>
            </div>

            <div style={{ textAlign:'center', color:C.grey, fontSize:12, fontWeight:700 }}>— or —</div>

            <div>
              <label style={{ fontSize:13, fontWeight:600, color:C.grey, display:'block', marginBottom:6 }}>Option B — Upload a signature image
                <span style={{ fontWeight:400, marginLeft:8 }}>(PNG transparent background, max 200KB)</span>
              </label>
              
              <div onClick={() => fileRef.current.click()} style={{
                border: `2px dashed ${sigPreview || hasExisting ? C.gold : C.border}`,
                borderRadius:12, padding:24, textAlign:'center', cursor:'pointer',
                background: sigPreview || hasExisting ? '#FFFBF0' : '#FAFAFA',
                minHeight:100, display:'flex', alignItems:'center', justifyContent:'center',
                transition:'all 0.2s',
              }}>
                {sigPreview ? (
                  <img src={sigPreview} alt="Signature preview" style={{ maxHeight:80, maxWidth:'80%', objectFit:'contain' }} />
                ) : hasExisting ? (
                  <div>
                    <div style={{ fontSize:24, marginBottom:6 }}>OK</div>
                    <div style={{ fontSize:13, color:C.grey }}>Signature uploaded — click to replace</div>
                  </div>
                ) : (
                  <div>
                    <div style={{ fontSize:32, marginBottom:6 }}>+</div>
                    <div style={{ fontSize:14, fontWeight:600, color:C.dark, marginBottom:4 }}>Upload Signature</div>
                    <div style={{ fontSize:12, color:C.grey }}>Click to select PNG file (transparent background works best)</div>
                  </div>
                )}
              </div>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg" onChange={handleFile} style={{ display:'none' }} />
              {sigPreview ? (
                <button onClick={() => { setSigFile(null); setSigPreview(null); }} style={{ marginTop:8, fontSize:12, color:'#EF4444', background:'none', border:'none', cursor:'pointer' }}>
                  Remove new selection
                </button>
              ) : hasExisting ? (
                <button onClick={removeSignature} style={{ marginTop:8, fontSize:12, color:'#EF4444', background:'none', border:'none', cursor:'pointer' }}>
                  ✕ Remove uploaded signature (use typed handwriting instead)
                </button>
              ) : null}
            </div>
          </div>
        </div>

        {/* How it appears preview */}
        <div className="card" style={{ padding:24, marginBottom:20, background:'#FFFBF0', border:`1px solid ${C.gold}40` }}>
          <h4 style={{ margin:'0 0 12px', fontSize:14, fontWeight:700, color:C.dark }}>How it appears on certificates</h4>
          <div style={{ display:'flex', justifyContent:'space-around', paddingTop:8 }}>
            <div style={{ textAlign:'center' }}>
              <div style={{ height:44, marginBottom:4, display:'flex', alignItems:'flex-end', justifyContent:'center' }}>
                {sigPreview ? (
                  <img src={sigPreview} alt="" style={{ maxHeight:40, maxWidth:140, objectFit:'contain' }} />
                ) : hasExisting ? (
                  <span style={{ fontSize:11, color:C.grey, fontStyle:'italic' }}>(your uploaded signature image)</span>
                ) : (
                  <span style={{ fontFamily:`'${sigFont}', cursive`, fontSize:30, color:'#15234a', lineHeight:1 }}>
                    {form.principal_name || 'Principal Name'}
                  </span>
                )}
              </div>
              <div style={{ borderTop:'1px solid #3A3A5A', paddingTop:6, width:140, margin:'0 auto' }}>
                <div style={{ fontSize:11, fontWeight:700 }}>{form.principal_title || 'School Principal'}</div>
                <div style={{ fontSize:10, color:C.grey, fontStyle:'italic' }}>{tenant?.name || 'Your School'}</div>
              </div>
            </div>
          </div>
        </div>

        <button onClick={save} disabled={saving} className="btn-teal" style={{ width:'100%', padding:'13px', fontSize:15 }}>
          {saving ? 'Saving...' : 'Save Certificate Settings'}
        </button>
        <button onClick={previewCert} disabled={previewing} style={{ width:'100%', padding:'13px', fontSize:15, marginTop:10, background:'#fff', color:C.teal, border:`2px solid ${C.teal}`, borderRadius:10, fontWeight:700, cursor:'pointer' }}>
          {previewing ? 'Generating preview…' : '👁 Preview Certificate'}
        </button>
        <p style={{ fontSize:12, color:C.grey, textAlign:'center', marginTop:8 }}>
          Opens a sample certificate (level B2, "Sample Student") with your settings. Save first to include a newly uploaded signature.
        </p>
      </div>
    </Layout>
  );
}
